/**
 * Iter-248 — Live Event Command Center
 *
 * Modernized GPS monitoring + activity feed + AI alerts for active shifts.
 * Polls /api/admin/live-ops/today every 20s.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  Header: title, refresh, last-updated timestamp                     │
 *   ├──────────────────────────┬──────────────────────────────────────────┤
 *   │  Shift selector (rail)   │  Map preview + Status legend              │
 *   │  + KPI tiles per shift   │  ──────────────────────────────────────  │
 *   │                          │  AI Alerts panel                          │
 *   │                          │  ──────────────────────────────────────  │
 *   │                          │  Talent pins grid (clickable cards)       │
 *   │                          │  ──────────────────────────────────────  │
 *   │                          │  Activity feed                            │
 *   └──────────────────────────┴──────────────────────────────────────────┘
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, RefreshCw, MapPin, Users, Radio, Clock,
  CheckCircle2, AlertTriangle, AlertCircle, Crown, Phone,
  ChevronRight, Loader2, Sparkles, Activity, Navigation,
} from 'lucide-react';
import { toast } from 'sonner';


const STATUS_COLORS = {
  green:  { bg: 'bg-emerald-500', ring: 'ring-emerald-400', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'On Site' },
  yellow: { bg: 'bg-amber-400',   ring: 'ring-amber-300',   dot: 'bg-amber-400',   text: 'text-amber-700',   label: 'Nearby / Expected' },
  red:    { bg: 'bg-rose-500',    ring: 'ring-rose-400',    dot: 'bg-rose-500',    text: 'text-rose-700',    label: 'Late / No-Show' },
  blue:   { bg: 'bg-sky-500',     ring: 'ring-sky-400',     dot: 'bg-sky-500',     text: 'text-sky-700',     label: 'Team Lead' },
};

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};


// ─── Talent pin card ──────────────────────────────────────────────────────
function TalentPinCard({ pin, onClick }) {
  const meta = STATUS_COLORS[pin.status_color] || STATUS_COLORS.yellow;
  return (
    <button
      onClick={onClick}
      data-testid={`live-pin-${pin.talent_id}`}
      className={`group relative flex items-center gap-3 p-3 rounded-xl border-2 bg-white hover:shadow-md transition text-left
                  ${pin.status_color === 'red'    ? 'border-rose-200    hover:border-rose-400' :
                    pin.status_color === 'green'  ? 'border-emerald-200 hover:border-emerald-400' :
                    pin.status_color === 'blue'   ? 'border-sky-300     hover:border-sky-500' :
                                                    'border-amber-200   hover:border-amber-400'}`}
    >
      <div className="relative shrink-0">
        {pin.photo ? (
          <img src={pin.photo} alt={pin.name}
               className={`w-12 h-12 rounded-full object-cover ring-2 ${meta.ring}`} />
        ) : (
          <div className={`w-12 h-12 rounded-full grid place-items-center text-white font-semibold ring-2 ${meta.ring} ${meta.bg}`}>
            {pin.name?.[0] || '?'}
          </div>
        )}
        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white ${meta.dot}`} />
        {pin.is_team_lead && (
          <Crown className="absolute -top-1.5 -right-1.5 w-4 h-4 text-amber-500 fill-amber-400 drop-shadow" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1">
          {pin.name}
        </div>
        <div className={`text-[11px] font-medium ${meta.text} truncate`}>
          {pin.status_label}
        </div>
        <div className="text-[10px] text-slate-400 truncate">
          {pin.role || '—'}
          {pin.distance_miles_from_venue != null && ` · ${pin.distance_miles_from_venue} mi`}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
    </button>
  );
}


// ─── Talent detail drawer ────────────────────────────────────────────────
function TalentDrawer({ pin, onClose }) {
  if (!pin) return null;
  const meta = STATUS_COLORS[pin.status_color] || STATUS_COLORS.yellow;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-end" data-testid="talent-drawer">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none shadow-2xl max-h-[88vh] overflow-y-auto">
        <div className={`p-5 ${meta.bg} text-white`}>
          <div className="flex items-center gap-3">
            {pin.photo ? (
              <img src={pin.photo} alt={pin.name} className="w-16 h-16 rounded-full object-cover ring-4 ring-white/40" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/20 grid place-items-center text-2xl font-bold">
                {pin.name?.[0] || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-lg font-bold flex items-center gap-1.5">
                {pin.name}
                {pin.is_team_lead && <Crown className="w-4 h-4 fill-amber-300" />}
              </div>
              <div className="text-sm opacity-90">{pin.status_label}</div>
              <div className="text-xs opacity-75 mt-0.5">{pin.role || '—'}</div>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Check-in</div>
              <div className="text-slate-900 font-medium">{formatTime(pin.clock_in_time)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Check-out</div>
              <div className="text-slate-900 font-medium">{formatTime(pin.clock_out_time)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Distance from venue</div>
              <div className="text-slate-900 font-medium">
                {pin.distance_miles_from_venue != null ? `${pin.distance_miles_from_venue} mi` : '—'}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Late by</div>
              <div className={`font-medium ${pin.is_late ? 'text-rose-700' : 'text-slate-900'}`}>
                {pin.is_late ? `${pin.late_minutes} min` : 'On time'}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Home city</div>
              <div className="text-slate-900 font-medium truncate">
                {[pin.city, pin.state].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">GPS pin</div>
              <div className="text-slate-900 font-medium truncate">
                {pin.lat != null && pin.lng != null ? `${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)}` : '—'}
              </div>
            </div>
          </div>
          {pin.phone && (
            <a href={`tel:${pin.phone}`} className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition"
               data-testid="drawer-phone-link">
              <Phone className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">{pin.phone}</span>
            </a>
          )}
          {pin.email && (
            <div className="text-xs text-slate-500 truncate">{pin.email}</div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="drawer-close">Close</Button>
        </div>
      </div>
    </div>
  );
}


// ─── Shift card (left rail) ─────────────────────────────────────────────
function ShiftCard({ shift, active, onClick }) {
  const ratio = shift.talent_needed > 0 ? (shift.checked_in_count / shift.talent_needed) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={`shift-card-${shift.shift_id}`}
      className={`w-full text-left p-3 rounded-xl border-2 transition ${
        active
          ? 'border-cyan-400 bg-cyan-50/60 shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-slate-500 truncate">
            {shift.client_business_name || '—'}
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate">
            {shift.event_name || shift.venue_name}
          </div>
          <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" />
            {shift.venue_name}
          </div>
        </div>
        {shift.ai_alerts?.length > 0 && (
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] shrink-0">
            {shift.ai_alerts.length} alert{shift.ai_alerts.length === 1 ? '' : 's'}
          </Badge>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className="text-slate-500">
          {shift.start_time?.slice(0,5)} – {shift.end_time?.slice(0,5)}
        </span>
        <span className="text-slate-700 font-medium">
          {shift.checked_in_count}/{shift.talent_needed} checked in
        </span>
      </div>
      <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-all ${ratio >= 1 ? 'bg-emerald-500' : ratio >= 0.5 ? 'bg-amber-400' : 'bg-rose-400'}`}
             style={{ width: `${Math.min(ratio * 100, 100)}%` }} />
      </div>
    </button>
  );
}


// ─── Main page ──────────────────────────────────────────────────────────
export default function LiveCommandCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeShiftId, setActiveShiftId] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const pollTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/live-ops/today');
      setData(res.data);
      setLastFetchedAt(new Date());
    } catch (e) {
      toast.error('Failed to load live ops data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-select the first shift once data arrives
  useEffect(() => {
    if (!activeShiftId && data?.shifts?.length > 0) {
      setActiveShiftId(data.shifts[0].shift_id);
    }
  }, [data, activeShiftId]);

  useEffect(() => {
    load();
    pollTimer.current = setInterval(load, 20000);
    return () => clearInterval(pollTimer.current);
  }, [load]);

  const activeShift = data?.shifts?.find(s => s.shift_id === activeShiftId) || null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="live-command-center">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin/operations')}
              data-testid="back-to-ops"
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Operations
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="relative inline-flex">
                  <Radio className="w-5 h-5 text-cyan-600" />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
                </span>
                Live Event Command Center
              </h1>
              <div className="text-[11px] text-slate-500 truncate">
                {data?.shift_count || 0} shift{data?.shift_count === 1 ? '' : 's'} running today
                {lastFetchedAt && ` · last updated ${lastFetchedAt.toLocaleTimeString()}`}
              </div>
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={load}
            data-testid="refresh-live-ops"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center h-[60vh] text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : !data?.shifts?.length ? (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 grid place-items-center mx-auto mb-4">
            <Radio className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">No shifts running today</h3>
          <p className="text-sm text-slate-500 mt-1">
            When events are active, you&apos;ll see live GPS pins, check-ins, and AI alerts here.
          </p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left rail — shift selector */}
          <aside className="lg:col-span-3 space-y-2" data-testid="shift-rail">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 px-1 mb-1">
              Today&apos;s shifts
            </div>
            {data.shifts.map(s => (
              <ShiftCard
                key={s.shift_id}
                shift={s}
                active={s.shift_id === activeShiftId}
                onClick={() => setActiveShiftId(s.shift_id)}
              />
            ))}
          </aside>

          {/* Main panel */}
          <main className="lg:col-span-9 space-y-4">
            {activeShift && (
              <>
                {/* Map placeholder + venue info */}
                <Card data-testid="map-card">
                  <CardContent className="p-0 overflow-hidden">
                    <div className="relative bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 h-56 sm:h-72 overflow-hidden">
                      {/* Stylized grid background that looks like a map */}
                      <svg className="absolute inset-0 w-full h-full opacity-60" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
                          </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                      </svg>

                      {/* Venue marker (center) */}
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-full bg-rose-500/20 animate-ping absolute -inset-2" />
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-rose-600 grid place-items-center text-white shadow-lg ring-4 ring-white">
                            <MapPin className="w-5 h-5" fill="currentColor" />
                          </div>
                        </div>
                      </div>

                      {/* Talent pins scattered around */}
                      {activeShift.talent_pins.map((p, idx) => {
                        // Angle + radius from center based on index for a clean layout
                        const angle = (idx / Math.max(activeShift.talent_pins.length, 1)) * Math.PI * 2;
                        const radius = p.status_color === 'green' ? 80 : p.status_color === 'yellow' ? 130 : 180;
                        const x = 50 + (radius * Math.cos(angle)) / 6;
                        const y = 50 + (radius * Math.sin(angle)) / 4;
                        const meta = STATUS_COLORS[p.status_color] || STATUS_COLORS.yellow;
                        return (
                          <button
                            key={p.talent_id}
                            onClick={() => setSelectedPin(p)}
                            className="absolute z-20"
                            style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                            data-testid={`map-pin-${p.talent_id}`}
                          >
                            <div className="relative group">
                              <div className={`w-8 h-8 rounded-full ${meta.bg} ring-2 ring-white shadow-md grid place-items-center text-white text-xs font-bold transition transform group-hover:scale-110`}>
                                {p.name?.[0] || '?'}
                              </div>
                              {p.is_team_lead && (
                                <Crown className="absolute -top-1.5 -right-1.5 w-3 h-3 text-amber-500 fill-amber-400" />
                              )}
                            </div>
                          </button>
                        );
                      })}

                      {/* Legend */}
                      <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-lg p-2 border border-slate-200 shadow-sm text-[10px]"
                           data-testid="map-legend">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" /> On site
                        </div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-2 h-2 rounded-full bg-amber-400" /> Nearby
                        </div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-2 h-2 rounded-full bg-rose-500" /> Late
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-sky-500" /> Team Lead
                        </div>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-slate-200">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Venue
                        </div>
                        <div className="text-sm font-semibold text-slate-900 truncate">{activeShift.venue_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{activeShift.venue_address}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Window
                        </div>
                        <div className="text-sm font-semibold text-slate-900">
                          {activeShift.start_time?.slice(0,5)} – {activeShift.end_time?.slice(0,5)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Staffing
                        </div>
                        <div className="text-sm font-semibold text-slate-900" data-testid="kpi-staffing">
                          {activeShift.staffed_count}/{activeShift.talent_needed}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Checked In
                        </div>
                        <div className="text-sm font-semibold text-emerald-700" data-testid="kpi-checked-in">
                          {activeShift.checked_in_count}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Alerts */}
                {activeShift.ai_alerts?.length > 0 && (
                  <Card data-testid="ai-alerts-card">
                    <CardContent className="p-4">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> AI Alerts
                      </div>
                      <div className="space-y-2">
                        {activeShift.ai_alerts.map((a, i) => (
                          <div
                            key={i}
                            className={`flex items-start gap-3 p-3 rounded-xl border ${
                              a.severity === 'high'
                                ? 'border-rose-200 bg-rose-50'
                                : a.severity === 'medium'
                                ? 'border-amber-200 bg-amber-50'
                                : 'border-sky-200 bg-sky-50'
                            }`}
                            data-testid={`ai-alert-${i}`}
                          >
                            <div className="text-xl shrink-0">{a.icon}</div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                              <div className="text-xs text-slate-700 mt-0.5">{a.body}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Talent pins */}
                <Card data-testid="talent-pins-card">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Talent on this shift
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {activeShift.talent_pins.map(p => (
                        <TalentPinCard key={p.talent_id} pin={p} onClick={() => setSelectedPin(p)} />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Activity feed */}
                <Card data-testid="activity-feed-card">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> Activity feed
                    </div>
                    {activeShift.activity_feed?.length === 0 ? (
                      <div className="text-xs text-slate-400 text-center py-4">
                        No activity yet for this shift.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {activeShift.activity_feed.map((a, i) => (
                          <div key={i} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition"
                               data-testid={`activity-${i}`}>
                            <span className="text-base shrink-0">{a.icon}</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-slate-900 truncate">
                                <span className="font-medium">{a.name}</span>
                                {' · '}
                                <span className="text-slate-600">{a.label}</span>
                              </div>
                            </div>
                            <div className="text-[10px] text-slate-400 shrink-0">{formatTime(a.ts)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </main>
        </div>
      )}

      {selectedPin && <TalentDrawer pin={selectedPin} onClose={() => setSelectedPin(null)} />}
    </div>
  );
}
